import { Box, Tooltip, Typography, Stack } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import React, { useMemo } from "react";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialColor, useMaterials } from "hooks/useMaterials";
import { AreaMaterialSnapshot } from "components/scrapEyeLabelling/types";
import { useAreaLabel } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { getLabellingMaterialDisplayName } from "components/scrapEyeLabelling/utils/materialDisplayName";

type TimelineAreaMappingMarkerProps = Readonly<{
  snapshot: AreaMaterialSnapshot;
  positionSeconds: number;
  duration: number;
  onClick?: (snapshot: AreaMaterialSnapshot) => void;
  isSelected?: boolean;
  location: string | undefined;
}>;

export const TimelineAreaMappingMarker = React.memo(
  ({
    snapshot,
    positionSeconds,
    duration,
    onClick,
    isSelected = false,
    location,
  }: TimelineAreaMappingMarkerProps) => {
    const { t } = useTenantTranslation();
    const { getColor } = useMaterialColor();
    const { materials } = useMaterials();
    const resolveAreaLabel = useAreaLabel(location);

    const positionPercent = useMemo(
      () => (positionSeconds / duration) * 100,
      [positionSeconds, duration]
    );

    const tooltipContent = useMemo(() => {
      const mappingEntries = Object.entries(snapshot.mapping);

      return (
        <Stack spacing={0.5} p={0.5}>
          <Typography variant="caption" fontWeight="bold">
            {t("areaMaterialMapping")}
          </Typography>
          <Typography variant="caption" fontSize="0.65rem">
            {snapshot.timestamp.utc().format("LTS")}
          </Typography>
          <Stack spacing={0.25} mt={0.5}>
            {mappingEntries.map(([area, material]) => (
              <Stack key={area} direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="caption"
                  fontSize="0.6rem"
                  sx={{ minWidth: 80 }}
                >
                  {resolveAreaLabel(area)}
                </Typography>
                <Box
                  sx={{
                    px: 0.5,
                    py: 0.125,
                    borderRadius: 0.5,
                    backgroundColor:
                      material === "unknown" ? "#000" : getColor(material),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.55rem",
                    }}
                  >
                    {getLabellingMaterialDisplayName(
                      materials?.find((m) => m.name === material) ?? null,
                      t
                    )}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
          <Typography
            variant="caption"
            fontSize="0.6rem"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            {t("clickToEdit")}
          </Typography>
        </Stack>
      );
      // eslint-disable-next-line @eslint-react/exhaustive-deps -- materials has unstable object identity (new array from query); snapshot already captures the material values used in rendering
    }, [snapshot, getColor, t, resolveAreaLabel]);

    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <Box
          onClick={() => onClick?.(snapshot)}
          sx={{
            position: "absolute",
            left: `${positionPercent}%`,
            top: 16,
            transform: "translateX(-50%)",
            cursor: onClick ? "pointer" : "default",
            zIndex: isSelected ? 20 : 5,
            "&:hover": {
              transform: "translateX(-50%) scale(1.2)",
              zIndex: 21,
            },
            transition: "transform 0.2s",
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: isSelected ? "primary.main" : "grey.700",
              border: "2px solid",
              borderColor: isSelected ? "primary.light" : "grey.500",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isSelected ? "0 0 8px rgba(25, 118, 210, 0.5)" : 1,
            }}
          >
            <SettingsIcon sx={{ fontSize: 12, color: "white" }} />
          </Box>
        </Box>
      </Tooltip>
    );
  }
);

TimelineAreaMappingMarker.displayName = "TimelineAreaMappingMarker";
